// eslint-disable-next-line import/no-extraneous-dependencies
import moment from "moment";
import React, {
  ChangeEvent,
  KeyboardEvent,
  MutableRefObject,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const DISPLAY_DATE_FORMAT = "DD/MM/YYYY";
const ACCEPTED_DATE_FORMATS = [DISPLAY_DATE_FORMAT, "D/M/YYYY"];
const CALENDAR_VERTICAL_OFFSET = 4;
const DEFAULT_CALENDAR_HEIGHT = 310;

interface useDatePickerProps {
  isShow: boolean;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  calendarPopupRef?: RefObject<HTMLDivElement>;
}

interface CalendarPosition {
  left: number;
  top: number;
}

interface useDatePickerReturnData {
  showCalendar: boolean;
  selectedDate: Date | undefined;
  inputValue: string;
  calendarPosition: CalendarPosition;
  datePickerRef: MutableRefObject<null | HTMLDivElement>;
  handleSelect: (selectedDate: moment.Moment) => void;
  handleOnChange: (selectedDate: moment.Moment | null) => void;
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleInputKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  handleCalendarInputClick: () => void;
  handleCalendarInputKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

export const useDatePicker = ({
  isShow,
  value,
  onChange,
  calendarPopupRef,
}: useDatePickerProps): useDatePickerReturnData => {
  const [showCalendar, setShowCalendar] = useState<boolean>(isShow);
  const [selectedDate, setSelectedDate] = useState(value);
  const [inputValue, setInputValue] = useState(selectedDate ? moment(selectedDate).format(DISPLAY_DATE_FORMAT) : "");
  const [calendarPosition, setCalendarPosition] = useState<CalendarPosition>({ left: 0, top: 0 });

  const datePickerRef = useRef<HTMLDivElement>(null);

  const updateCalendarPosition = (): void => {
    if (!datePickerRef.current) {
      return;
    }

    const { left, top, bottom } = datePickerRef.current.getBoundingClientRect();
    const popupHeight = DEFAULT_CALENDAR_HEIGHT;
    const spaceBelow = window.innerHeight - bottom;
    const shouldOpenAbove = spaceBelow < popupHeight + CALENDAR_VERTICAL_OFFSET;
    const viewportTop = window.scrollY + CALENDAR_VERTICAL_OFFSET;
    const nextTop = shouldOpenAbove
      ? Math.max(viewportTop, top + window.scrollY - popupHeight - CALENDAR_VERTICAL_OFFSET)
      : bottom + window.scrollY + CALENDAR_VERTICAL_OFFSET;

    setCalendarPosition({
      left: left + window.scrollX,
      top: nextTop,
    });
  };

  const handleSelect = (dateSelected: moment.Moment): void => {
    if (dateSelected) {
      setShowCalendar(false);
      setSelectedDate(dateSelected.toDate());
      onChange(dateSelected.toDate());
    } else {
      setSelectedDate(undefined);
      onChange(undefined);
    }
  };

  const handleOnChange = (dateSelected: moment.Moment | null): void => {
    if (dateSelected) {
      // removing the local time and converting to javascript date
      const jsDate = dateSelected.utcOffset(0, true).toDate();

      setSelectedDate(jsDate);
      onChange(jsDate);
      return;
    }

    setSelectedDate(undefined);
    onChange(undefined);
  };

  const handleCalendarInputClick = (): void => {
    if (!showCalendar) {
      updateCalendarPosition();
      setShowCalendar(true);
    }
  };

  useEffect(() => {
    if (value !== selectedDate) {
      setSelectedDate(value);
    }
  }, [value]);

  useEffect(() => {
    setInputValue(selectedDate ? moment(selectedDate).format(DISPLAY_DATE_FORMAT) : "");
  }, [selectedDate]);

  useEffect(() => {
    setShowCalendar(isShow);
  }, [isShow]);

  const handleCalendarInputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    switch (e.key) {
      case "Enter":
        updateCalendarPosition();
        setShowCalendar(!showCalendar);
        break;
      case "Escape":
        setShowCalendar(false);
        break;
      default:
        break;
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const nextValue = event.target.value;
    setInputValue(nextValue);

    if (!nextValue.trim()) {
      handleOnChange(null);
      return;
    }

    const parsedDate = moment(nextValue, ACCEPTED_DATE_FORMATS, true);
    if (parsedDate.isValid()) {
      handleOnChange(parsedDate);
    }
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Escape") {
      handleCalendarInputKeyDown(event as unknown as KeyboardEvent<HTMLDivElement>);
      return;
    }

    if (event.key === "Enter") {
      const parsedDate = moment(inputValue, ACCEPTED_DATE_FORMATS, true);
      if (parsedDate.isValid()) {
        handleOnChange(parsedDate);
      }

      handleCalendarInputKeyDown(event as unknown as KeyboardEvent<HTMLDivElement>);
    }
  };

  useLayoutEffect(() => {
    if (!showCalendar) {
      return undefined;
    }

    updateCalendarPosition();

    const handleWindowChange = (): void => {
      updateCalendarPosition();
    };

    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);

    return () => {
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [showCalendar]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;

      if (datePickerRef.current?.contains(target) || calendarPopupRef?.current?.contains(target)) {
        return;
      }

      setShowCalendar(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [calendarPopupRef]);

  return {
    showCalendar,
    selectedDate,
    inputValue,
    calendarPosition,
    datePickerRef,
    handleSelect,
    handleOnChange,
    handleInputChange,
    handleInputKeyDown,
    handleCalendarInputClick,
    handleCalendarInputKeyDown,
  };
};
